import { useEffect, useState } from "react";
import config from "../config/index.js";

const STORAGE_KEY = "gilder.geofence.v1";
const CACHE_TTL_MS = 6 * 60 * 60 * 1000; // 6h — geo doesn't change often
const FETCH_TIMEOUT_MS = 4000;

/**
 * Public IP→country resolvers tried IN ORDER. ipapi.co is the default
 * but is commonly blocked by ad blockers (uBlock Origin's
 * "Privacy" list ships it), so we fall through to two alternatives.
 * Each returns an upper-case ISO 3166-1 alpha-2 country code or
 * throws to advance to the next resolver.
 */
const RESOLVERS = [
    {
        name: "ipapi.co",
        url: "https://ipapi.co/json/",
        extract: (data) => data && (data.country_code || data.country)
    },
    {
        name: "ipwho.is",
        url: "https://ipwho.is/",
        extract: (data) => (data && data.success !== false ? data.country_code : null)
    },
    {
        name: "ipinfo.io",
        url: "https://ipinfo.io/json",
        extract: (data) => data && data.country
    }
];

/**
 * Returns the user's detected country code + whether the protocol UI
 * should be blocked because that jurisdiction is on the restricted list
 * (`config.restrictedRegions`, e.g. ["US", "SG"]).
 *
 * Caches the resolution in `localStorage` for 6 hours so we don't hit
 * the IP resolver on every page load.
 *
 * Returns: { country, blocked, isLoading, error }
 *
 * Dev testing from a restricted region uses the BUILD-TIME env flag
 * `VITE_GEOFENCE_DISABLED=true` — there is deliberately NO user-clickable
 * override, so a visitor in a blocked jurisdiction cannot self-bypass.
 */
export function useGeofence() {
    const [country, setCountry] = useState(null);
    const [blocked, setBlocked] = useState(false);
    const [isLoading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function resolve() {
            try {
                // Build-time dev bypass only. This is baked in at build
                // time from the environment — it is NOT something a
                // visitor can toggle, so production users in restricted
                // regions cannot use it to get through.
                if (import.meta.env.VITE_GEOFENCE_DISABLED === "true") {
                    if (!cancelled) {
                        setCountry("DEV");
                        setBlocked(false);
                        setLoading(false);
                    }
                    return;
                }

                const cached = readCache();
                if (cached) {
                    if (!cancelled) {
                        applyResolution(cached.country, setCountry, setBlocked);
                        setLoading(false);
                    }
                    return;
                }

                const code = await resolveWithFallback();
                if (cancelled) return;
                if (code) {
                    writeCache({ country: code });
                    applyResolution(code, setCountry, setBlocked);
                    setLoading(false);
                } else {
                    // Every resolver failed (ad blocker, rate limit,
                    // offline). Fail open — `country` stays null,
                    // `blocked` stays false. The disclaimer modal still
                    // forces the user to declare their country manually.
                    setError("All IP resolvers unreachable");
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || String(err));
                    setLoading(false);
                }
            }
        }
        resolve();
        return () => {
            cancelled = true;
        };
    }, []);

    return { country, blocked, isLoading, error };
}

async function resolveWithFallback() {
    for (const resolver of RESOLVERS) {
        try {
            const code = await tryResolver(resolver);
            if (code) return code;
        } catch (err) {
            // Log to console so a user / dev can see which resolver
            // is being blocked — usually an ad blocker. Continue to
            // the next resolver.
            if (typeof console !== "undefined") {
                console.warn(`[gilder] geofence resolver ${resolver.name} failed:`, err.message || err);
            }
        }
    }
    return null;
}

async function tryResolver({ url, extract }) {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeout = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null;
    try {
        const resp = await fetch(url, {
            method: "GET",
            // No credentials — these are public endpoints; sending
            // cookies trips the CORS preflight.
            credentials: "omit",
            signal: controller ? controller.signal : undefined
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        const raw = extract(data);
        const code = String(raw || "").toUpperCase();
        if (!code || code === "UNDEFINED" || !/^[A-Z]{2}$/.test(code)) {
            throw new Error("No country in response");
        }
        return code;
    } finally {
        if (timeout) clearTimeout(timeout);
    }
}

function applyResolution(code, setCountry, setBlocked) {
    setCountry(code);
    const restricted = (config.restrictedRegions || []).map((c) => c.toUpperCase());
    setBlocked(restricted.includes(code));
}

function readCache() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (Date.now() - (data.timestamp || 0) > CACHE_TTL_MS) return null;
        // Stale-from-prior-version guard: only honour the cache when it
        // carries an actual ISO country code. An empty / null entry from
        // an older build (or a failed resolver pre-fallback) would
        // otherwise pin the modal to "no detected country" until TTL.
        if (!data.country || !/^[A-Z]{2}$/.test(String(data.country).toUpperCase())) return null;
        return data;
    } catch {
        return null;
    }
}

function writeCache(data) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...data, timestamp: Date.now() }));
    } catch {
        /* private browsing — fine to skip */
    }
}
